import { APP_CONFIG } from '@/lib/app/config';
import { APIError } from '@/lib/api/APIError';
import type { APIErrorCode, APIFieldError } from '@/lib/api/apiTypes';
import { trackApiError } from '@/lib/analytics/posthog';
import { captureSentryException } from '@/lib/analytics/sentry';
import Constants from 'expo-constants';

export type AnalyticsRequestInit = RequestInit & {
  analyticsAction?:
    | 'unknown'
    | 'load_feed'
    | 'load_event'
    | 'load_search'
    | 'load_profile'
    | 'submit_rsvp';
};

export { APIError } from '@/lib/api/APIError';

export function getAPIFieldError(error: unknown, field: string): APIFieldError | null {
  if (!(error instanceof APIError)) return null;
  return error.fieldErrors.find((fieldError) => fieldError.field === field) ?? null;
}

export function getAPIFieldErrorMessage(error: unknown, field: string): string | null {
  return getAPIFieldError(error, field)?.message ?? null;
}

export function getAPIBaseURL() {
  return APP_CONFIG.apiBaseURL;
}

const REQUEST_TIMEOUT_MS = 15000;
const APP_VERSION = Constants.expoConfig?.version;
const UUID_SEGMENT_PATTERN =
  /\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|$)/gi;

function analyticsEndpoint(path: string) {
  const pathname = path.split(/[?#]/, 1)[0] || path;
  return pathname.replace(UUID_SEGMENT_PATTERN, '/:id');
}

function normalizeFieldErrors(value: unknown): APIFieldError[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    if (
      typeof record.field !== 'string' ||
      typeof record.message !== 'string' ||
      typeof record.code !== 'string'
    ) {
      return [];
    }
    return [
      {
        field: record.field,
        code: record.code as APIErrorCode,
        message: record.message,
      },
    ];
  });
}

export async function requestJSON<T>(
  path: string,
  init: AnalyticsRequestInit = {},
  accessToken?: string | null
): Promise<T> {
  const url = `${getAPIBaseURL()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (APP_VERSION) {
    headers.set('X-App-Version', APP_VERSION);
  }
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  const abortListener = () => controller.abort();
  init.signal?.addEventListener('abort', abortListener);

  let response: Response;
  const { analyticsAction, ...requestInit } = init;
  try {
    response = await fetch(url, {
      ...requestInit,
      headers,
      signal: controller.signal,
    });
  } catch (error) {
    trackApiError({
      endpoint: analyticsEndpoint(path),
      failureType: (error as Error)?.name === 'AbortError' ? 'timeout' : 'network',
      statusCode: 0,
      userAction: analyticsAction,
    });
    captureSentryException(error, {
      tags: { area: 'api', kind: 'network' },
      extra: { path, method: init.method ?? 'GET' },
    });
    if ((error as Error)?.name === 'AbortError') {
      throw new Error(
        `Unable to reach API at ${getAPIBaseURL()} within ${REQUEST_TIMEOUT_MS / 1000}s. ` +
          'If you are testing on a phone, make sure the backend is running on your computer and listening on your LAN IP.'
      );
    }
    const reason = error instanceof Error ? error.message : 'Network request failed';
    throw new Error(`Unable to reach API at ${getAPIBaseURL()}. ${reason}`);
  } finally {
    clearTimeout(timeoutId);
    init.signal?.removeEventListener('abort', abortListener);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const responseRecord =
      payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
    const message =
      typeof responseRecord?.error === 'string'
        ? responseRecord.error
        : typeof responseRecord?.message === 'string'
          ? responseRecord.message
          : 'Request failed';
    const code = typeof responseRecord?.code === 'string' ? responseRecord.code : undefined;
    const requestID =
      (typeof responseRecord?.request_id === 'string' ? responseRecord.request_id : undefined) ??
      response.headers.get('X-Request-Id') ??
      undefined;
    const apiError = new APIError(
      message,
      response.status,
      code,
      {
        fieldErrors: normalizeFieldErrors(responseRecord?.field_errors),
        requestID,
      }
    );
    // 401s are handled transparently by the auth store's token refresh logic — don't track them
    // as errors since the retry succeeds and they would generate noise in analytics.
    if (response.status !== 401) {
      trackApiError({
        endpoint: analyticsEndpoint(path),
        failureType: 'http',
        code,
        requestID,
        statusCode: response.status,
        userAction: analyticsAction,
      });
    }
    if (response.status >= 500) {
      captureSentryException(apiError, {
        tags: { area: 'api', kind: 'server' },
        extra: { path, method: init.method ?? 'GET', status: response.status },
      });
    }
    throw apiError;
  }

  return payload as T;
}
